import { useState } from 'react'
import { Outlet, NavLink, useLocation } from 'react-router-dom'
import { cn } from '@/lib/cn'
import {
  Bars3Icon,
  ClipboardDocumentListIcon,
  BuildingOffice2Icon,
  SignalIcon,
  HeartIcon,
  MapPinIcon,
  ClockIcon,
  ChartBarIcon,
  ArchiveBoxIcon,
  WrenchScrewdriverIcon,
  ShieldCheckIcon,
  CodeBracketIcon,
} from '@heroicons/react/24/outline'

type NavItem = { name: string; href: string; icon: React.ComponentType<React.SVGProps<SVGSVGElement>> }
type NavSection = { section: string; items: NavItem[] }
type NavEntry = NavItem | NavSection

const navigation: NavEntry[] = [
  { name: 'Dashboard', href: '/dashboard', icon: ChartBarIcon },
  { name: 'Disponibilites', href: '/availability', icon: ClipboardDocumentListIcon },
  { name: 'Structures', href: '/structures', icon: BuildingOffice2Icon },
  { name: 'Orientation', href: '/orientation', icon: MapPinIcon },
  { name: 'Historique', href: '/history', icon: ClockIcon },
  { name: 'Stocks critiques', href: '/stocks', icon: ArchiveBoxIcon },
  { name: 'Monitoring HL7', href: '/monitoring', icon: SignalIcon },
  {
    section: 'Tooling',
    items: [
      { name: 'Verification CMU', href: '/tooling/cmu-check', icon: ShieldCheckIcon },
    ],
  },
  {
    section: 'Admin',
    items: [
      { name: 'Éditeur Templates', href: '/admin/templates', icon: CodeBracketIcon },
    ],
  },
]

function getAllNavItems(entries: NavEntry[]): NavItem[] {
  return entries.flatMap((e) => ('section' in e ? e.items : [e]))
}

export default function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const location = useLocation()

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 bg-black/30 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-neutral-200 transform transition-transform duration-300 lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Logo */}
        <div className="h-16 flex items-center gap-3 px-5 border-b border-neutral-200">
          <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary-600">
            <HeartIcon className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-sm font-bold text-neutral-900 leading-tight">JoinXroad</h1>
            <p className="text-xs text-primary-600 font-medium">Sante</p>
          </div>
        </div>

        {/* Navigation */}
        <nav className="mt-4 px-3 space-y-1">
          {navigation.map((entry) => {
            if ('section' in entry) {
              return (
                <div key={entry.section} className="pt-4">
                  <div className="flex items-center gap-2 px-3 mb-2">
                    <WrenchScrewdriverIcon className="w-3.5 h-3.5 text-neutral-400" />
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-neutral-400">
                      {entry.section}
                    </span>
                  </div>
                  {entry.items.map((item) => {
                    const isActive = location.pathname === item.href
                    return (
                      <NavLink
                        key={item.href}
                        to={item.href}
                        onClick={() => setSidebarOpen(false)}
                        className={cn(
                          'flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-150',
                          isActive
                            ? 'bg-primary-50 text-primary-700'
                            : 'text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900'
                        )}
                      >
                        <item.icon className={cn('w-5 h-5', isActive ? 'text-primary-600' : 'text-neutral-400')} />
                        {item.name}
                      </NavLink>
                    )
                  })}
                </div>
              )
            }
            const isActive = location.pathname === entry.href
            return (
              <NavLink
                key={entry.href}
                to={entry.href}
                onClick={() => setSidebarOpen(false)}
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-150',
                  isActive
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900'
                )}
              >
                <entry.icon className={cn('w-5 h-5', isActive ? 'text-primary-600' : 'text-neutral-400')} />
                {entry.name}
              </NavLink>
            )
          })}
        </nav>

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-neutral-200">
          <div className="flex items-center gap-2 text-xs text-neutral-400">
            <div className="w-2 h-2 rounded-full bg-success-500" />
            <span>Connecte -- Dakar</span>
          </div>
          <p className="text-xs text-neutral-300 mt-1">Digiboost 2026</p>
        </div>
      </aside>

      {/* Main area */}
      <div className="lg:pl-64">
        {/* Header */}
        <header className="sticky top-0 z-30 h-16 bg-white border-b border-neutral-200 flex items-center justify-between px-4 lg:px-6">
          <button
            className="lg:hidden p-2 rounded-md text-neutral-600 hover:bg-neutral-100"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="w-6 h-6" />
          </button>

          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-neutral-900 hidden sm:block">
              {getAllNavItems(navigation).find((n) => n.href === location.pathname)?.name || 'JoinXroad Sante'}
            </h2>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-xs text-neutral-400 hidden sm:inline">
              Light Portal -- Saisie des disponibilites
            </span>
          </div>
        </header>

        {/* Content */}
        <main className="min-h-[calc(100vh-4rem)]">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
